import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../contexts/auth';
import { Link } from 'wouter';
import ComboboxAutocompleteAPI from './combobox_autocomplete_api';
import { getEmployeesWithActiveContract } from '../services/api_calendar';

const InactiveEmployeesTable = () => {
    const { token, profile } = useContext(AuthContext);
    const [filteredEmployees, setFilteredEmployees] = useState(null);
    const [totalResultNotActiveContract, setTotalResultNotActiveContract] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [pageNotActiveContract, setPageNotActiveContract] = useState(1);
    const [pageSizeNotActiveContract, setPageSizeNotActiveContract] = useState(5);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeesWithInactiveContract, setEmployeesWithInactiveContract] = useState([]);

    useEffect(() => {
        const fetchInactiveEmployees = async () => {
            setIsLoading(true);
            try {
                const response = await getEmployeesWithActiveContract(token, null, 'contract_not_active', null, pageNotActiveContract, pageSizeNotActiveContract);
                setEmployeesWithInactiveContract(response.results || []);
                setTotalResultNotActiveContract(response.count || 0);
            } catch (error) {
                console.error('Errore nel recupero dei dipendenti inattivi:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInactiveEmployees();
    }, [token, pageNotActiveContract, pageSizeNotActiveContract]);

    const handleEmployeeSelection = (employee) => {
        setSelectedEmployee(employee);
        if (employee) {
            setFilteredEmployees([employee]);
        } else {
            setFilteredEmployees(null);
        }
    };

    const handleResetSelection = () => {
        setSelectedEmployee(null);
        setFilteredEmployees(null);
    };
    
    const handlePreviousPageInactive = () => {
        if (pageNotActiveContract > 1) setPageNotActiveContract(pageNotActiveContract - 1);
    };

    const handleNextPageInactive = () => {
        if (pageNotActiveContract * pageSizeNotActiveContract < totalResultNotActiveContract) {
            setPageNotActiveContract(pageNotActiveContract + 1);
        }
    };

    return (
        <>
            <div className='pb-8 flex justify-center'>
                <div className='container mx-4 px-3 rounded-md mt-5'>
                    <div className='grid grid-cols-3 gap-4 pb-4 items-center'>  
                        <div className='col-span-1 flex items-center'>
                            <p className='text-xs font-medium text-gray-600'>
                                Per riattivare il dipendente, seleziona il nome e aggiungi un contratto attivo.
                            </p>
                        </div>

                        <div className='col-span-1 flex justify-end items-center'>
                            {selectedEmployee && (
                                <button 
                                    className='rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                    onClick={handleResetSelection}
                                >
                                    Reset selezione
                                </button>
                            )}
                        </div>

                        <div className="col-span-1">
                            <ComboboxAutocompleteAPI
                                getData={async (query) => {
                                    if (query.trim() === '') return Promise.resolve([]);

                                    try {
                                        const res = await getEmployeesWithActiveContract(token, query, 'contract_not_active', null, 1, 5);
                                        return res.results || [];
                                    } catch (error) {
                                        console.error('Errore durante il recupero dei dati:', error);
                                        return [];
                                    }
                                }}
                                getDisplay={(employee) => employee.last_name && employee.first_name
                                    ? `${employee.last_name} ${employee.first_name}`
                                    : employee.name || employee.cf || ''
                                }
                                getId={(employee) => employee.id}
                                onChange={handleEmployeeSelection}
                                currentValue={selectedEmployee}
                                placeholder="Cerca dipendente..."
                            />
                        </div>
                    </div>
            
                    <div className="overflow-x-auto max-h-60">
                        {isLoading ? (
                            <div className="flex justify-center border rounded-md p-4">
                                <p className="text-sm font-semibold text-gray-600 px-6 py-4">Caricamento...</p>
                            </div>
                        ) : employeesWithInactiveContract.length === 0 ? (
                            <div className="flex justify-center border rounded-md p-4">
                                <p className="text-sm font-semibold text-gray-600 px-6 py-4">Nessun risultato</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200 border rounded-md">
                                <thead className="bg-white m-0 py-2 sticky top-0 z-10">
                                    <tr>
                                        <th className="w-1/3 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codice Fiscale</th>
                                        <th className="w-1/3 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cognome</th>
                                        <th className="w-1/3 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {(filteredEmployees || employeesWithInactiveContract)
                                    .sort((a, b) => (a.last_name || a.cf).localeCompare(b.last_name || b.cf))
                                    .map((employee) => (
                                        <Link key={employee.id} to={`/dipendenti/${employee.cf}/edit?tab=2`}>
                                            <tr className="cursor-pointer hover:bg-indigo-50">
                                                <td className="w-1/3 px-3 py-2 whitespace-nowrap text-xs font-semibold text-gray-600">{employee.cf}</td>
                                                <td className="w-1/3 px-3 py-2 whitespace-nowrap text-sm font-normal text-gray-600">{employee.last_name}</td>
                                                <td className="w-1/3 px-3 py-2 whitespace-nowrap text-sm font-normal text-gray-600">{employee.first_name}</td>
                                            </tr>
                                        </Link>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {filteredEmployees === null && totalResultNotActiveContract > pageSizeNotActiveContract && (
                <div className='pb-8 flex justify-center'>
                    <div className='container mx-4 px-3 rounded-md mt-5'>

                        <div className="flex justify-between my-4 px-8">
                            <button
                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                onClick={handlePreviousPageInactive}
                                disabled={pageNotActiveContract === 1}
                            >
                                Precedente
                            </button>
                            <span className="text-sm font-medium text-gray-900">
                                Pagina {pageNotActiveContract} di {Math.ceil(totalResultNotActiveContract / pageSizeNotActiveContract)}
                            </span>
                            <button
                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                onClick={handleNextPageInactive}
                                disabled={pageNotActiveContract * pageSizeNotActiveContract >= totalResultNotActiveContract}
                            >
                                Successivo
                            </button>
                        </div>
                    </div> 
                </div>
            )}
        </>
    );
};

export default InactiveEmployeesTable;
